import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Box, Button, Card, CardContent, CircularProgress, Divider, IconButton, TextField, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, PushPin as PinIcon, PushPinOutlined as UnpinIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';

interface Note {
  _id: string;
  title: string;
  content: string;
  entityType: 'client' | 'property' | 'utilityService' | 'general';
  entityId: string;
  isPinned: boolean;
  isPrivate: boolean;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  updatedBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface NotesPanelProps {
  entityType: 'client' | 'property' | 'utilityService' | 'general';
  entityId: string;
  title?: string;
}

const NotesPanel: React.FC<NotesPanelProps> = ({ entityType, entityId, title = 'Notes' }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form states
  const [showForm, setShowForm] = useState<boolean>(false);
  const [noteTitle, setNoteTitle] = useState<string>('');
  const [noteContent, setNoteContent] = useState<string>('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  
  // Fetch notes
  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/notes/entity/${entityType}/${entityId}`);
      setNotes(response.data.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching notes:', err);
      setError(err.response?.data?.message || 'Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);
  
  // Initial fetch
  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingNoteId) {
        // Update existing note
        await api.put(`/notes/${editingNoteId}`, {
          title: noteTitle,
          content: noteContent
        });
      } else {
        // Create new note
        await api.post('/notes', {
          title: noteTitle,
          content: noteContent,
          entityType,
          entityId,
          isPinned: false,
          isPrivate: false
        });
      }
      
      // Reset form and refresh notes
      resetForm();
      fetchNotes();
    } catch (err: any) {
      console.error('Error saving note:', err);
      setError(err.response?.data?.message || 'Failed to save note');
    }
  };
  
  // Reset form
  const resetForm = () => {
    setNoteTitle('');
    setNoteContent('');
    setEditingNoteId(null);
    setShowForm(false);
  };
  
  // Edit note
  const handleEdit = (note: Note) => {
    setNoteTitle(note.title);
    setNoteContent(note.content);
    setEditingNoteId(note._id);
    setShowForm(true);
  };
  
  // Delete note
  const handleDelete = async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      await api.delete(`/notes/${noteId}`);
      fetchNotes();
    } catch (err: any) {
      console.error('Error deleting note:', err);
      setError(err.response?.data?.message || 'Failed to delete note');
    }
  };
  
  // Toggle pin status
  const handleTogglePin = async (noteId: string) => {
    try {
      await api.patch(`/notes/${noteId}/toggle-pin`);
      fetchNotes();
    } catch (err: any) {
      console.error('Error toggling pin status:', err);
      setError(err.response?.data?.message || 'Failed to update pin status');
    }
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };
  
  // Check if user can edit/delete a note
  const canModifyNote = (note: Note) => {
    return note.createdBy && note.createdBy._id === user?._id;
  };
  
  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{title}</Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : 'Add Note'}
          </Button>
        </Box>
        
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        
        {showForm && (
          <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
            <TextField
              label="Title"
              fullWidth
              margin="normal"
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
              required
            />
            <TextField
              label="Content"
              fullWidth
              margin="normal"
              multiline
              rows={4}
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              required
            />
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button type="submit" variant="contained" color="primary">
                {editingNoteId ? 'Update Note' : 'Add Note'}
              </Button>
              <Button variant="outlined" onClick={resetForm}>
                Cancel
              </Button>
            </Box>
          </Box>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : notes.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
            No notes found. Click "Add Note" to create one.
          </Typography>
        ) : (
          <Box>
            {notes.map((note) => (
              <Card 
                key={note._id} 
                variant="outlined" 
                sx={{ 
                  mb: 2, 
                  backgroundColor: note.isPinned ? 'rgba(255, 223, 0, 0.05)' : 'inherit',
                  borderLeft: note.isPinned ? '4px solid #FFD700' : 'inherit'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="h6">{note.title}</Typography>
                    <Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleTogglePin(note._id)}
                        title={note.isPinned ? "Unpin note" : "Pin note"}
                      >
                        {note.isPinned ? <PinIcon color="primary" /> : <UnpinIcon />}
                      </IconButton>
                      
                      {canModifyNote(note) && (
                        <>
                          <IconButton 
                            size="small" 
                            onClick={() => handleEdit(note)}
                            title="Edit note"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDelete(note._id)}
                            title="Delete note"
                          >
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Box>
                  
                  <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                    {note.content}
                  </Typography>
                  
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="textSecondary">
                      By {note.createdBy ? `${note.createdBy.firstName} ${note.createdBy.lastName}` : 'Unknown User'} • {formatDate(note.createdAt)}
                    </Typography>
                    {note.updatedAt !== note.createdAt && (
                      <Typography variant="caption" color="textSecondary">
                        Updated {formatDate(note.updatedAt)}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default NotesPanel;
